import { isNil } from 'lodash-es';
/**
 * A service class that provides functionality for managing column sizes in a grid.
 */
export class ColumnSizingService {
    static combineWithMinSizes(sizes, minSizes, maxSizes, width) {
        const maxLength = Math.max(sizes.length, minSizes.length, maxSizes.length);
        return Array.from({ length: maxLength }, (_, i) => i).map((_, index) => {
            const size = sizes[index];
            let minSize = minSizes[index]
                ? minSizes[index]
                : ColumnSizingService.COLUMN_DEFAULT_MIN_WIDTH;
            let maxSize = maxSizes[index];
            // Convert percentage-based min sizes to pixels
            if (minSize.endsWith('%')) {
                const percent = parseFloat(minSize);
                const pixelValue = Math.floor((width * percent) / 100);
                minSize = `${pixelValue}px`;
            }
            // Convert percentage-based max sizes to pixels
            if (maxSize && maxSize.endsWith('%')) {
                const percent = parseFloat(maxSize);
                const pixelValue = Math.ceil((width * percent) / 100);
                maxSize = `${pixelValue}px`;
            }
            if (!size) {
                return `minmax(${minSize},${maxSize ? maxSize : '100%'})`;
            }
            if (size.endsWith('fr')) {
                return `${size}`;
            }
            return `clamp(${minSize},${size}, ${maxSize ? maxSize : '100%'})`;
        });
    }
    static simplifySizes(sizeString) {
        return sizeString.replaceAll(/repeat\((\d+),\s*([\d.]+[a-z%]+)\)/g, (match, count, value) => {
            return Array(Number(count)).fill(value).join(' ');
        });
    }
    /**
     * Creates a new instance of the ColumnSizingService class.
     * @param {() => {columns: number[]; width: number}} liveSizes - A function that returns an object with live column sizes and grid width.
     */
    constructor(liveSizes) {
        this.liveSizes = liveSizes;
        this.minSizes = [];
        this.maxSizes = [];
        this.sizes = [];
        this.preResizingSizes = [];
        this.resizingSizes = [];
        this.state = State.NOT_RESIZING;
        this.alreadyResizedColumnIndexes = new Set();
    }
    /**
     * Updates the column sizes based on the given size string and column count.
     * @param {string | undefined | null} sizeString - The size string containing space-separated column sizes.
     * @param {number} colCount - The number of columns.
     */
    updateSizes(sizeString, colCount) {
        if (!isNil(sizeString)) {
            this.sizes = this.expandArrayToSize(ColumnSizingService.simplifySizes(sizeString).split(' '), colCount);
        }
        else {
            this.sizes = [];
        }
        if (!this.initialSizes || this.initialSizes.length !== colCount) {
            this.initialSizes = [...this.sizes];
        }
    }
    /**
     * Resizes multiple columns by distributing the delta value among the specified column indices.
     * @param {number[]} columnIndices - An array of column indices to resize.
     * @param {number} delta - The total resizing delta value.
     * @param {'ltr' | 'rtl'} dir - The text direction ('ltr' or 'rtl').
     */
    resizeColumns(columnIndices, delta, dir) {
        const distributedDelta = delta / columnIndices.length;
        for (const columnIndex of columnIndices) {
            this.resizeColumn(columnIndex, distributedDelta, dir);
        }
    }
    /**
     * Resizes a single column by the specified delta value based on the text direction.
     * @param {number} index - The index of the column to resize.
     * @param {number} delta - The resizing delta value.
     * @param {'ltr' | 'rtl'} dir - The text direction ('ltr' or 'rtl').
     */
    resizeColumn(index, delta, dir) {
        const maxSizes = this.getLiveMaxSize();
        const minSizes = this.getLiveMinSizes();
        const expectedSizeOfColumnAfterResize = this.preResizingSizes[index] + delta * this.dirSign(dir);
        if (delta !== 0) {
            this.alreadyResizedColumnIndexes.add(index);
        }
        if ((minSizes[index] && expectedSizeOfColumnAfterResize < minSizes[index]) ||
            (maxSizes[index] && expectedSizeOfColumnAfterResize > maxSizes[index])) {
            return;
        }
        const preResizingSizesSum = this.resizingSizes.reduce((acc, current) => {
            return acc + parseFloat(current);
        }, 0);
        const { width } = this.liveSizes();
        if (delta < 0 && preResizingSizesSum > width) {
            const newSize = Number((this.preResizingSizes[index] + delta * this.dirSign(dir)).toFixed(4));
            this.resizingSizes[index] = `${newSize}px`;
            return;
        }
        const nonFixedColumnIndexes = [];
        this.sizes.forEach((size, i) => {
            if ((!this.initialSizes ||
                !this.initialSizes[index] ||
                (!this.initialSizes[i].endsWith('px') &&
                    !this.alreadyResizedColumnIndexes.has(i))) &&
                i > index) {
                nonFixedColumnIndexes.push(i);
            }
        });
        const nonFixedColumnSizesSum = nonFixedColumnIndexes.reduce((acc, i) => {
            acc += this.preResizingSizes[i];
            return acc;
        }, 0);
        nonFixedColumnIndexes.forEach((i) => {
            const currentColumnSize = this.preResizingSizes[i];
            const currentColumnPercent = (currentColumnSize / nonFixedColumnSizesSum) * 100.0;
            const deltaPerColumn = (delta * currentColumnPercent) / 100;
            const newSize = currentColumnSize - deltaPerColumn * this.dirSign(dir);
            this.resizingSizes[i] = `${Number(newSize.toFixed(4))}px`;
        });
        const newSize = Number((this.preResizingSizes[index] + delta * this.dirSign(dir)).toFixed(4));
        this.resizingSizes[index] = `${newSize}px`;
    }
    expandCollapseColumn(delta, dir) {
        this.startResizing();
        this.resizeColumn(0, delta, dir);
        return this.resizingSizes;
    }
    /**
     * Updates the minimum column sizes based on the given min size string and column count.
     * @param {string | undefined | null} minSizeString - The min size string containing space-separated minimum column sizes.
     * @param {number} colCount - The number of columns.
     */
    updateMinSizes(minSizeString, colCount) {
        if (!isNil(minSizeString)) {
            this.minSizes = this.expandArrayToSize(ColumnSizingService.simplifySizes(minSizeString).split(' '), colCount);
        }
        else {
            this.minSizes = [];
        }
    }
    /**
     * Updates the maximum column sizes based on the given min size string and column count.
     * @param {string | undefined | null} maxSizeString - The min size string containing space-separated minimum column sizes.
     * @param {number} colCount - The number of columns.
     */
    updateMaxSizes(maxSizeString, colCount) {
        if (!isNil(maxSizeString)) {
            this.maxSizes = this.expandArrayToSize(ColumnSizingService.simplifySizes(maxSizeString).split(' '), colCount);
        }
        else {
            this.maxSizes = [];
        }
    }
    /**
     * Updates the column count and ensures the sizes and minSizes arrays have the same length as the new column count.
     * @param {number} columnCount - The new number of columns.
     */
    updateColumnCount(columnCount) {
        this.sizes = this.expandArrayToSize(this.sizes, columnCount);
        if (this.minSizes.length > 0) {
            this.minSizes = this.expandArrayToSize(this.minSizes, columnCount);
        }
    }
    /**
     * Starts the column resizing process and initializes the resizingSizes array.
     */
    startResizing() {
        this.state = State.RESIZING;
        const { columns } = this.liveSizes();
        this.preResizingSizes = [...columns];
        this.resizingSizes = [...columns.map(col => `${col}px`)];
    }
    /**
     * Finishes the column resizing process, updates the sizes array, and clears the resizingSizes array.
     */
    finishResizing(calculatePercentage = true) {
        this.state = State.NOT_RESIZING;
        const { columns, width } = this.liveSizes();
        this.resizingSizes = [];
        this.preResizingSizes = [];
        this.sizes = columns.map((col, index) => {
            if (!this.sizes[index].endsWith('px') &&
                !this.sizes[index].endsWith('fr') &&
                calculatePercentage) {
                return `${((col / width) * 100.0).toFixed(4)}%`;
            }
            else {
                return `${col}px`;
            }
        });
    }
    /**
     * Returns the current column sizes, combined with the minimum column sizes if applicable.
     * @returns {string[]} An array of column sizes as strings.
     */
    getSizes() {
        const sizes = this.state === State.RESIZING && this.resizingSizes.length
            ? this.resizingSizes
            : this.sizes;
        const { width } = this.liveSizes();
        return ColumnSizingService.combineWithMinSizes(sizes, this.minSizes, this.maxSizes, width);
    }
    getMinSizes() {
        return this.minSizes.length > 0 ? this.minSizes : undefined;
    }
    getRawSizes() {
        return this.sizes;
    }
    getLiveMinSizes() {
        const { width } = this.liveSizes();
        return this.minSizes.map((current) => {
            if (current.endsWith('%')) {
                const percent = parseFloat(current);
                return Math.floor((width * percent) / 100);
            }
            return parseFloat(current);
        });
    }
    getLiveMaxSize() {
        const { width } = this.liveSizes();
        return this.maxSizes.map((current) => {
            if (current.endsWith('%')) {
                const percent = parseFloat(current);
                return Math.ceil((width * percent) / 100);
            }
            return parseFloat(current);
        });
    }
    calculateColumnNewSizesDuringGridContainerResizing(gridContainerNewWidth, gridContainerOldWidth) {
        const difference = gridContainerNewWidth - gridContainerOldWidth;
        if (difference === 0 || gridContainerNewWidth < 0) {
            return;
        }
        const columnsSizesSum = this.sizes.reduce((acc, currentValue) => acc + parseFloat(currentValue), 0);
        this.sizes.forEach((current, index) => {
            if (!this.initialSizes ||
                !this.initialSizes[index] ||
                (!this.initialSizes[index].endsWith('px') &&
                    !this.alreadyResizedColumnIndexes.has(index))) {
                const columnSize = parseFloat(current);
                const percent = this.resolveColumnPercent(gridContainerOldWidth, columnSize, columnsSizesSum, index);
                const newSize = (gridContainerNewWidth * percent) / 100.0;
                this.sizes[index] = `${Number(newSize.toFixed())}px`;
            }
        });
    }
    resolveColumnPercent(gridContainerOldWidth, columnSize, columnsSizesSum, index) {
        if (this.initialSizes &&
            this.initialSizes[index] &&
            this.initialSizes[index].endsWith('%')) {
            return parseFloat(this.initialSizes[index]);
        }
        return gridContainerOldWidth != 0
            ? (columnSize / gridContainerOldWidth) * 100.0
            : (columnSize / columnsSizesSum) * 100;
    }
    dirSign(dir) {
        return dir === 'ltr' ? 1 : -1;
    }
    expandArrayToSize(from, size) {
        let lastSize = '';
        const result = [];
        for (let i = 0; i < size; i++) {
            if (!isNil(from) && from.length > i) {
                lastSize = from[i];
            }
            result[i] = lastSize;
        }
        return result;
    }
}
ColumnSizingService.COLUMN_DEFAULT_MIN_WIDTH = '120px';
var State;
(function (State) {
    State[State["RESIZING"] = 0] = "RESIZING";
    State[State["NOT_RESIZING"] = 1] = "NOT_RESIZING";
})(State || (State = {}));
